import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { aboutValues } from "@/mocks/faqs";
import { testimonials } from "@/mocks/testimonials";
import { siteInfo } from "@/mocks/site";

const process = [
  { step: "01", title: "收集公开信息", desc: "只保留可标注来源的媒体、公告或公开记录。" },
  { step: "02", title: "对齐时间顺序", desc: "把同一事件的不同说法放到同一条时间线上。" },
  { step: "03", title: "区分事实与推测", desc: "逐条标注来源状态，不确定的部分单独说明。" },
  { step: "04", title: "持续更新", desc: "出现新进展时补充内容并标注更新时间。" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 17爆料网内容原则与团队介绍",
    description:
      "了解17爆料网的品牌定位、内容原则与制作流程。我们基于公开信息整理热点事件，按时间线还原来龙去脉，不发布个人隐私信息。",
    keywords: "关于17爆料网,内容原则,热点整理,团队介绍",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 17爆料网",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "17爆料网",
        email: siteInfo.email,
        description: siteInfo.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17baoliao.example.com/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://17baoliao.example.com/about" },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <div className="w-full px-4 pt-6 md:px-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        </div>

        <section className="w-full px-4 pb-10 pt-6 md:px-8">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-bookmark-3-line text-base"></i>
                品牌介绍
              </span>
              <h1 className="mt-4 font-heading text-2xl font-black leading-tight tracking-tight text-foreground-950 md:text-4xl">
                关于 {siteInfo.name}
              </h1>
              <p className="mt-5 text-base leading-relaxed text-foreground-700">{siteInfo.intro}</p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                我们相信，读者不需要被替着下结论，只需要一个把信息摆放整齐的地方。把同一事件的多方说法按时间对齐，读者自然能看清事情的走向。
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/topics"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  浏览热点整理
                  <i className="ri-arrow-right-line text-base"></i>
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-foreground-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
                >
                  联系我们
                </Link>
              </div>
            </div>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20an%20editorial%20desk%20with%20timeline%20cards%20and%20magnifier%2C%20warm%20vermilion%20olive%20and%20cream%20palette%2C%20flat%20geometric%20shapes%2C%20calm%20organized%20composition%2C%20magazine%20aesthetic%2C%20high%20detail&width=1200&height=900&seq=bl-about-team-01&orientation=landscape"
                alt="17爆料网编辑整理工作的示意图"
                title="17爆料网 - 内容整理流程"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="w-full px-4 py-12 md:px-8 md:py-16">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">网站定位</h2>
            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950">面向谁</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  面向关注社会热点、消费话题与网络现象的普通读者，尤其是希望快速了解事件全貌、不愿被碎片信息牵着走的人。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950">解决什么</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  把同一事件中散落各处的说法集中到一个页面，用小标题与时间线替代情绪堆积，降低理解成本。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950">不做什么</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  不发布个人隐私信息，不做指向个人的推断，不用未经核实的材料制造结论。有争议的部分一律标注为待确认。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">内容原则</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            下面三条是我们对每篇内容的最低要求，也是内部复核时的判断标准。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {aboutValues.map((value) => (
              <article key={value.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${value.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{value.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="border-y border-background-200 bg-secondary-950 text-background-100">
          <div className="w-full px-4 py-12 md:px-8 md:py-16">
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">制作流程</h2>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-background-300">
              从发现线索到发布整理，一篇内容大致会经过四个步骤。
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {process.map((item) => (
                <div key={item.step} className="rounded-lg border border-secondary-800 bg-secondary-900 p-5">
                  <span className="font-heading text-2xl font-black text-accent-400">{item.step}</span>
                  <h3 className="mt-3 font-heading text-base font-bold text-background-50">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-background-300">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">读者怎么说</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            来自不同行业读者的使用反馈，帮助我们调整内容方向。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                <i className="ri-double-quotes-l text-3xl text-primary-300"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.content}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-700">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="w-full px-4 pb-14 md:px-8">
          <div className="flex flex-col items-start gap-6 rounded-lg border border-background-200 bg-background-100 p-6 md:flex-row md:items-center md:justify-between md:p-10">
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950">联系方式</h2>
              <div className="mt-3 flex flex-col gap-2 text-sm text-foreground-600 sm:flex-row sm:flex-wrap sm:gap-x-6">
                <span className="flex items-center gap-2">
                  <i className="ri-mail-line text-base text-secondary-600"></i>
                  <a href={`mailto:${siteInfo.email}`} className="transition-colors hover:text-primary-600 cursor-pointer">
                    {siteInfo.email}
                  </a>
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line text-base text-secondary-600"></i>
                  {siteInfo.workingHours}
                </span>
              </div>
            </div>
            <Link
              to="/contact"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              前往联系页面
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}